// apps/merchant-v3/src/lib/format/datetime.ts
//
// Phase 6 Wave 2 Task 06 — Datetime formatter helpers.
//
//  + plan task-06 acceptance criteria:
//   - `formatRelative(iso)`         — "5 phút trước" / "2 hours ago"
//                                     for ages < 24h. Uses
//                                     `Intl.RelativeTimeFormat`.
//   - `formatAbsolute(iso, …)`      — DD/MM/YYYY for ages ≥ 7d, with
//                                     locale-aware time component.
//   - `formatSmart(iso, …)`         — combines both; <24h → relative,
//                                     1–6d → "Yesterday/Monday", ≥7d →
//                                     absolute.
//   - `useUserTimezone()`           — reads user.timezone Zustand or
//                                     falls back to browser-resolved
//                                     timezone via Intl.
//   - `getTimezoneList()`           — curated short list shipped with
//                                     the Settings dropdown (Wave 11).
//
// vg-binding: PLAN:task-06
// vg-binding: P6.D-131
// vg-binding: P6.D-221

import { useUser } from '@/store/user';
import { localeFromUserLang } from './currency';

// --- types -----------------------------------------------------------------

export type DateTimeFormat = 'short' | 'medium' | 'long';

export interface FormatDateTimeOptions {
  timezone?: string;
  locale?: string;
  format?: DateTimeFormat;
}

// --- helpers ---------------------------------------------------------------

const ONE_MINUTE = 60_000;
const ONE_HOUR = 60 * ONE_MINUTE;
const ONE_DAY = 24 * ONE_HOUR;
const ONE_WEEK = 7 * ONE_DAY;

function safeParse(iso: string): Date | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t);
}

function resolveBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// --- public formatters -----------------------------------------------------

/**
 * Format an absolute date+time. Uses `Intl.DateTimeFormat` with the
 * provided timezone and locale. The `format` arg maps to
 * `dateStyle/timeStyle` width pairs.
 */
export function formatDateTime(
  iso: string,
  opts: FormatDateTimeOptions = {},
): string {
  const date = safeParse(iso);
  if (!date) return '—';
  const { timezone = resolveBrowserTimezone(), locale = 'en-US', format = 'medium' } = opts;
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    dateStyle: format,
    timeStyle: format,
  }).format(date);
}

/**
 * Absolute DD/MM/YYYY. Optional `withTime` appends a
 * locale-aware HH:mm component.
 */
export function formatAbsolute(
  iso: string,
  opts: FormatDateTimeOptions & { withTime?: boolean } = {},
): string {
  const date = safeParse(iso);
  if (!date) return '—';
  const { timezone = resolveBrowserTimezone(), locale = 'en-US', withTime = false } = opts;
  const dateStr = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
  if (!withTime) return dateStr;
  const timeStr = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return `${dateStr} ${timeStr}`;
}

/**
 * Relative time ("5 phút trước" / "2 hours ago") via Intl.RelativeTimeFormat.
 * Falls back to the absolute string when the iso > 24h old.
 */
export function formatRelative(
  iso: string,
  opts: FormatDateTimeOptions & { now?: number } = {},
): string {
  const date = safeParse(iso);
  if (!date) return '—';
  const { locale = 'en-US', now = Date.now() } = opts;
  const diffMs = date.getTime() - now;
  const absMs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (absMs < ONE_MINUTE) {
    return rtf.format(Math.round(diffMs / 1000), 'second');
  }
  if (absMs < ONE_HOUR) {
    return rtf.format(Math.round(diffMs / ONE_MINUTE), 'minute');
  }
  if (absMs < ONE_DAY) {
    return rtf.format(Math.round(diffMs / ONE_HOUR), 'hour');
  }
  if (absMs < ONE_WEEK) {
    return rtf.format(Math.round(diffMs / ONE_DAY), 'day');
  }
  return formatAbsolute(iso, opts);
}

/**
 * Smart formatter — relative when <24h, "Yesterday/Monday"-style for the
 * 1-6d window via numeric:'auto', absolute DD/MM/YYYY when ≥ 7d.
 */
export function formatSmart(
  iso: string,
  opts: FormatDateTimeOptions & { now?: number } = {},
): string {
  const date = safeParse(iso);
  if (!date) return '—';
  const now = opts.now ?? Date.now();
  const absMs = Math.abs(date.getTime() - now);
  if (absMs >= ONE_WEEK) return formatAbsolute(iso, opts);
  return formatRelative(iso, { ...opts, now });
}

// --- timezone helpers ------------------------------------------------------

const COMMON_TIMEZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho Chi Minh (UTC+7)' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (UTC+7)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (UTC+10)' },
  { value: 'Europe/London', label: 'Europe/London (UTC+0)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
  { value: 'America/New_York', label: 'America/New York (UTC-5)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (UTC-8)' },
];

export function getTimezoneList(): ReadonlyArray<{ value: string; label: string }> {
  return COMMON_TIMEZONES;
}

/** Hook returning the active timezone — user pref → browser → UTC. */
export function useUserTimezone(): string {
  const user = useUser();
  return user?.timezone ?? resolveBrowserTimezone();
}

/** Hook returning a memo-stable formatter bound to user TZ + locale. */
export function useDateTimeFormatter(): {
  timezone: string;
  locale: string;
  formatRelative: (iso: string) => string;
  formatAbsolute: (iso: string, opts?: { withTime?: boolean }) => string;
  formatSmart: (iso: string) => string;
  formatDateTime: (iso: string, format?: DateTimeFormat) => string;
} {
  const user = useUser();
  const timezone = user?.timezone ?? resolveBrowserTimezone();
  const locale = localeFromUserLang(user?.language);
  return {
    timezone,
    locale,
    formatRelative: (iso) => formatRelative(iso, { timezone, locale }),
    formatAbsolute: (iso, opts) => formatAbsolute(iso, { timezone, locale, ...opts }),
    formatSmart: (iso) => formatSmart(iso, { timezone, locale }),
    formatDateTime: (iso, format = 'medium') =>
      formatDateTime(iso, { timezone, locale, format }),
  };
}

export { resolveBrowserTimezone };
